/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect } from 'react';
import BackButton from '../common/BackButton';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { ParticipantType, Participant, Company } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import '../bids/CreateBid.css';

const AdminParticipantForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const { currentUser } = useAuth();

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    title: '',
    contactNote: '',
    type: ParticipantType.USER,
    managerId: '',
    companyId: '',
    userInactive: false,
  });
  const [managers, setManagers] = useState<Participant[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Load managers and companies
      const [participantsResponse, companiesResponse] = await Promise.all([
        apiClient.getAllParticipants(),
        apiClient.getAllCompanies(),
      ]);

      if (participantsResponse.success && participantsResponse.participants) {
        const managerList = participantsResponse.participants.filter(
          p => p.type === ParticipantType.MANAGER
        );
        setManagers(managerList);
      }

      if (companiesResponse.success && companiesResponse.companies) {
        setCompanies(companiesResponse.companies);
      }

      // Load participant if in edit mode
      if (isEditMode) {
        const response = await apiClient.getParticipantById(id!);

        if (!response.success || !response.participant) {
          setError('Failed to load participant');
          return;
        }

        const participant = response.participant;
        setFormData({
          username: participant.username || '',
          email: participant.email || '',
          firstName: participant.firstName || '',
          lastName: participant.lastName || '',
          title: participant.title || '',
          contactNote: participant.contactNote || '',
          type: participant.type || ParticipantType.USER,
          managerId: participant.managerId?.toString() || '',
          companyId: participant.companyId?.toString() || '',
          userInactive: participant.userInactive || false,
        });
      }
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsSaving(true);

    try {
      if (isEditMode) {
        // Update existing participant
        const request = {
          type: formData.type,
          firstName: formData.firstName,
          lastName: formData.lastName,
          title: formData.title,
          contactNote: formData.contactNote,
          managerId: formData.managerId ? Number(formData.managerId) : undefined,
          companyId: formData.companyId ? Number(formData.companyId) : undefined,
          userInactive: formData.userInactive,
        };

        const response = await apiClient.updateParticipant(id!, request);

        if (response.success) {
          setSuccessMessage('Participant updated successfully');
          setTimeout(() => navigate('/participants'), 1500);
        } else {
          setError(response.error || 'Failed to update participant');
        }
      } else {
        // Create new participant
        const request = {
          username: formData.username,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          title: formData.title,
          contactNote: formData.contactNote,
          type: formData.type,
          managerId: formData.managerId ? Number(formData.managerId) : undefined,
          companyId: formData.companyId ? Number(formData.companyId) : undefined,
          // Password will be set via email link - no need to provide it here
        };

        const response = await apiClient.createParticipant(request);

        if (response.success) {
          setSuccessMessage('Participant created successfully');
          setTimeout(() => navigate('/participants'), 1500);
        } else {
          setError(response.error || 'Failed to create participant');
        }
      }
    } catch (err: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} participant:`, err);
      setError(err.response?.data?.error || err.message || `An error occurred while ${isEditMode ? 'updating' : 'creating'} participant`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/participants');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (isLoading) {
    return (
      <div className="create-bid-container">
      <BackButton />
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const requiresManager = formData.type === ParticipantType.USER;
  const canHaveCompany = formData.type !== ParticipantType.ADMIN;

  return (
    <div className="create-bid-container">
      <div className="create-bid-header">
        <h2>{isEditMode ? 'Edit Participant' : 'Create Participant'}</h2>
      </div>

      {!isEditMode && currentUser?.demoParticipant && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '6px',
          color: '#856404'
        }}>
          <strong>Demo Account Restriction:</strong> Demo users cannot create new participants. This form is read-only for demonstration purposes.
        </div>
      )}

      <form onSubmit={handleSubmit} className="create-bid-form">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {successMessage && (
          <div style={{
            padding: '1rem',
            marginBottom: '1rem',
            backgroundColor: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '6px',
            color: '#155724'
          }}>
            {successMessage}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="type">Type *</label>
          <select
            id="type"
            name="type"
            value={formData.type}
            onChange={handleChange}
            required
            disabled={isSaving}
          >
            <option value={ParticipantType.USER}>User</option>
            <option value={ParticipantType.MANAGER}>Manager</option>
            <option value={ParticipantType.ADMIN}>Admin</option>
            <option value={ParticipantType.EXECUTIVE}>Executive</option>
          </select>
          {formData.type === ParticipantType.ADMIN && (
            <small style={{ color: '#666', fontSize: '0.85rem' }}>
              Admins cannot be assigned to a company
            </small>
          )}
          {formData.type === ParticipantType.EXECUTIVE && (
            <small style={{ color: '#666', fontSize: '0.85rem' }}>
              Executives can be assigned to a company but do not report to a manager
            </small>
          )}
        </div>

        {isEditMode && (
          <div className="form-group">
            <label>Username</label>
            <input
              type="text"
              value={formData.username}
              readOnly
              disabled
              style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
            />
          </div>
        )}

        {!isEditMode && (
          <>
            <div className="form-group">
              <label htmlFor="username">Username *</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                disabled={isSaving}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isSaving}
              />
              <small style={{ color: '#666', fontSize: '0.85rem' }}>
                A welcome email with password setup link will be sent to this address
              </small>
            </div>
          </>
        )}

        <div className="form-group">
          <label htmlFor="firstName">First Name *</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            disabled={isSaving}
          />
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Last Name *</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            disabled={isSaving}
          />
        </div>

        <div className="form-group">
          <label htmlFor="title">Title</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            disabled={isSaving}
          />
        </div>

        <div className="form-group">
          <label htmlFor="contactNote">Contact Notes</label>
          <textarea
            id="contactNote"
            name="contactNote"
            value={formData.contactNote}
            onChange={handleChange}
            rows={4}
            disabled={isSaving}
            style={{ minHeight: '100px', resize: 'vertical' }}
          />
        </div>

        {requiresManager && (
          <div className="form-group">
            <label htmlFor="managerId">Manager *</label>
            <select
              id="managerId"
              name="managerId"
              value={formData.managerId}
              onChange={handleChange}
              required
              disabled={isSaving}
            >
              <option value="">Select a manager</option>
              {managers.map(manager => (
                <option key={manager.id} value={manager.id}>
                  {manager.firstName} {manager.lastName} ({manager.username})
                </option>
              ))}
            </select>
          </div>
        )}

        {canHaveCompany && (
          <div className="form-group">
            <label htmlFor="companyId">Company {(formData.type === ParticipantType.MANAGER || formData.type === ParticipantType.EXECUTIVE) ? '*' : ''}</label>
            <select
              id="companyId"
              name="companyId"
              value={formData.companyId}
              onChange={handleChange}
              required={formData.type === ParticipantType.MANAGER || formData.type === ParticipantType.EXECUTIVE}
              disabled={isSaving}
            >
              <option value="">None</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {isEditMode && (
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="userInactive"
                checked={formData.userInactive}
                onChange={handleChange}
                disabled={isSaving}
                style={{ marginRight: '0.5rem' }}
              />
              Mark as Inactive
            </label>
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSaving}
            className="cancel-button"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="submit-button"
          >
            {isSaving ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create Participant')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminParticipantForm;
